<script setup lang="ts">
import { ref, computed, watch, nextTick } from 'vue'
import { useSvgInteraction } from '../../composables/useSvgInteraction'
import SkeletonLoader from '../shared/SkeletonLoader.vue'

const props = defineProps<{
  svgContent: string | null
  isLoading: boolean
  level: 'vehicle' | 'mainGroup' | 'subGroup'
  hiddenIds?: string[]
  hiddenItems?: { id: string; mode: string }[]
}>()

const emit = defineEmits<{
  areaClicked: [id: string, name: string]
  areaHovered: [id: string, name: string]
  areaLeft: []
}>()

const containerRef = ref<HTMLElement | null>(null)
const tooltipText = ref('')
const tooltipVisible = ref(false)
const tooltipX = ref(0)
const tooltipY = ref(0)
const isMobile = ref(window.innerWidth < 768)

const {
  scale,
  translateX,
  translateY,
  isDragging,
  handleClick,
  handleMouseOver,
  handleMouseOut,
  handleTouchStart,
  handleTouchMove,
  handleTouchEnd,
  zoomIn,
  zoomOut,
  resetZoom,
} = useSvgInteraction({
  containerRef,
  onAreaClick(elementId) {
    const navId = svgMeta.value.clickMap.get(elementId) || elementId
    // Skip clicks on hidden categories
    if (isHiddenNavId(navId)) return
    const name = svgMeta.value.tooltipMap.get(elementId) || elementId
    emit('areaClicked', navId, name)
  },
  onAreaHover(elementId, fallbackName) {
    const navId = svgMeta.value.clickMap.get(elementId) || elementId
    if (isHiddenNavId(navId)) return
    const resolvedName = svgMeta.value.tooltipMap.get(elementId) || fallbackName
    tooltipText.value = resolvedName
    tooltipVisible.value = true
    emit('areaHovered', elementId, resolvedName)
  },
  onAreaLeave() {
    tooltipVisible.value = false
    emit('areaLeft')
  },
})

// Parse all <a> elements from raw SVG to extract:
// - clickOnElement('h3') → navigation ID
// - showToolTip('Motor', ...) → tooltip name
// Keyed by the <a> element's id attribute
function parseSvgMeta(svg: string) {
  const clickMap = new Map<string, string>()
  const tooltipMap = new Map<string, string>()

  // Use a single regex to find <a ...> tags with their full attributes
  const anchorRegex = /<a\s([^>]+)>/gi
  let anchorMatch
  while ((anchorMatch = anchorRegex.exec(svg)) !== null) {
    const attrs = anchorMatch[1]
    const idMatch = attrs.match(/id="([^"]+)"/)
    if (!idMatch) continue
    const elId = idMatch[1]

    const clickMatch = attrs.match(/onclick="clickOnElement\s*\(\s*'([^']+)'/)
    if (clickMatch) clickMap.set(elId, clickMatch[1])

    const tipMatch = attrs.match(/onmouseover="showToolTip\s*\(\s*'([^']+)'/)
    if (tipMatch) tooltipMap.set(elId, tipMatch[1])
  }

  return { clickMap, tooltipMap }
}

// Strip inline event handlers from SVG so our event delegation takes over
function sanitizeSvg(svg: string): string {
  return svg.replace(/\s+on(click|mouseover|mouseout|mousemove|mouseenter|mouseleave)="[^"]*"/gi, '')
}

const svgMeta = computed(() => props.svgContent ? parseSvgMeta(props.svgContent) : { clickMap: new Map(), tooltipMap: new Map() })
const cleanSvg = computed(() => props.svgContent ? sanitizeSvg(props.svgContent) : null)

// Check if a navigation ID (h5, s3, c267) maps to a hidden category
function isHiddenNavId(navId: string): boolean {
  const hidden = props.hiddenIds ?? []
  if (!hidden.length) return false
  if (navId.startsWith('h')) return hidden.includes(`mg:${navId.substring(1)}`)
  if (navId.startsWith('s')) return hidden.some(h => h.startsWith('sg:') && h.endsWith(`:${navId.substring(1)}`))
  if (navId.startsWith('c')) return hidden.some(h => h.startsWith('item:') && h.endsWith(`:${navId.substring(1)}`))
  return false
}

// Resolve hide mode for a given hidden category ID
function getHideMode(hiddenCategoryId: string): string {
  const item = (props.hiddenItems ?? []).find(h => h.id === hiddenCategoryId)
  return item?.mode ?? 'whitebox'
}

// Resolve the hidden category ID that matches a navId
function getMatchingHiddenId(navId: string): string | null {
  const hidden = props.hiddenIds ?? []
  if (navId.startsWith('h')) {
    const id = `mg:${navId.substring(1)}`
    return hidden.includes(id) ? id : null
  }
  if (navId.startsWith('s')) {
    return hidden.find(h => h.startsWith('sg:') && h.endsWith(`:${navId.substring(1)}`)) ?? null
  }
  if (navId.startsWith('c')) {
    return hidden.find(h => h.startsWith('item:') && h.endsWith(`:${navId.substring(1)}`)) ?? null
  }
  return null
}

// After SVG renders, apply hiding to hidden clickable areas
watch(cleanSvg, () => {
  nextTick(() => {
    if (!containerRef.value || !props.hiddenIds?.length) return
    const svgEl = containerRef.value.querySelector('svg')
    if (!svgEl) return

    const clickMap = svgMeta.value.clickMap
    for (const [elId, navId] of clickMap.entries()) {
      const hiddenId = getMatchingHiddenId(navId)
      if (!hiddenId) continue

      const el = containerRef.value.querySelector(`#${CSS.escape(elId)}`)
      if (!el) continue

      const mode = getHideMode(hiddenId)

      if (mode === 'opacity') {
        // Opacity mode: fade out and disable
        ;(el as SVGElement).style.opacity = '0'
        ;(el as SVGElement).style.pointerEvents = 'none'
      } else {
        // Whitebox mode: cover with white rectangle
        try {
          const bbox = (el as SVGGraphicsElement).getBBox()
          if (bbox.width === 0 && bbox.height === 0) continue

          const ns = 'http://www.w3.org/2000/svg'
          const pad = 3
          const overlay = document.createElementNS(ns, 'rect')
          overlay.setAttribute('x', String(bbox.x - pad))
          overlay.setAttribute('y', String(bbox.y - pad))
          overlay.setAttribute('width', String(bbox.width + pad * 2))
          overlay.setAttribute('height', String(bbox.height + pad * 2))
          overlay.setAttribute('fill', 'rgba(255, 255, 255, 1)')
          overlay.setAttribute('rx', '4')
          overlay.setAttribute('style', 'pointer-events: none;')

          el.parentNode?.insertBefore(overlay, el.nextSibling)
          ;(el as SVGElement).style.pointerEvents = 'none'
        } catch {
          // getBBox can throw if element is not rendered
        }
      }
    }
  })
})

const transformStyle = computed(() => ({
  transform: `translate(${translateX.value}px, ${translateY.value}px) scale(${scale.value})`,
  transformOrigin: '0 0',
  cursor: isDragging.value ? 'grabbing' : (scale.value > 1 ? 'grab' : 'pointer'),
}))

function onMouseMove(e: MouseEvent) {
  tooltipX.value = e.clientX + 15
  tooltipY.value = e.clientY + 15
}

// Reset zoom when content changes
watch(() => props.svgContent, () => {
  resetZoom()
})

window.addEventListener('resize', () => {
  isMobile.value = window.innerWidth < 768
})
</script>

<template>
  <div class="svg-viewer" ref="containerRef">
    <!-- Zoom controls -->
    <div class="svg-viewer__controls" v-if="cleanSvg">
      <button @click="zoomIn" aria-label="Zoom inn" title="Zoom inn">+</button>
      <button @click="zoomOut" aria-label="Zoom ut" title="Zoom ut">&minus;</button>
      <button @click="resetZoom" aria-label="Tilbakestill" title="Tilbakestill">
        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"/>
          <path d="M3 3v5h5"/>
        </svg>
      </button>
    </div>

    <!-- Loading skeleton -->
    <SkeletonLoader v-if="isLoading" type="svg" />

    <!-- SVG container -->
    <div
      v-else-if="cleanSvg"
      class="svg-viewer__canvas"
      :style="transformStyle"
      @click="handleClick"
      @mouseover="handleMouseOver"
      @mouseout="handleMouseOut"
      @mousemove="onMouseMove"
      @touchstart.passive="handleTouchStart"
      @touchmove.prevent="handleTouchMove"
      @touchend="handleTouchEnd"
      v-html="cleanSvg"
    />

    <!-- Empty state -->
    <div v-else-if="!isLoading" class="svg-viewer__empty">
      <p>Velg en kategori for å se bildeldiagram</p>
    </div>

    <!-- Tooltip -->
    <Teleport to="body">
      <div
        v-if="tooltipVisible && tooltipText"
        class="svg-tooltip"
        :style="{ left: tooltipX + 'px', top: tooltipY + 'px' }"
      >
        {{ tooltipText }}
      </div>
    </Teleport>
  </div>
</template>

<style scoped lang="scss">
.svg-viewer {
  position: relative;
  overflow: hidden;
  border-radius: 8px;
  background: white;
  border: 1px solid #e5e7eb;
  min-height: 300px;

  &__controls {
    position: absolute;
    top: 10px;
    right: 10px;
    z-index: 10;
    display: flex;
    flex-direction: column;
    gap: 4px;

    button {
      width: 32px;
      height: 32px;
      border: 1px solid #d1d5db;
      background: white;
      border-radius: 6px;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 1.1rem;
      font-weight: 600;
      box-shadow: 0 1px 3px rgba(0,0,0,0.1);

      &:hover {
        background: #f3f4f6;
      }
    }
  }

  &__canvas {
    width: 100%;
    transition: transform 0.05s linear;

    :deep(svg) {
      width: 100%;
      height: auto;
      display: block;
    }

    :deep(a) {
      cursor: pointer;
    }
  }

  &__empty {
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 300px;
    color: #999;
    font-size: 0.95rem;
  }
}
</style>

<style>
.svg-tooltip {
  position: fixed;
  z-index: 9999;
  pointer-events: none;
  background: #1f2937;
  color: white;
  padding: 6px 12px;
  border-radius: 6px;
  font-size: 0.85rem;
  white-space: nowrap;
  box-shadow: 0 4px 12px rgba(0,0,0,0.15);
}
</style>
